import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

import './Filters.scss';

import { H2, Text, Paragraph } from 'components/core/Typography';
import { Checkbox } from 'components/core/Checkbox';
import { Button } from 'components/atoms';
import { Image } from 'components/core/Image';
import { Popup, StorageUnitSizeModal, Modal } from '../../../molecules';
import filtersImage from 'assets/images/icons/filters.svg';
import ChevronRight from 'assets/images/icons/chevron-right.svg';
import scale from 'assets/images/icons/scale.svg';

// Import helpers from separate files
import { SIZE_CATEGORIES, parseSqft, categorizeUnit } from './SizeFilters';
import { extractAmenities, unitHasAmenity } from './FeaturesFilters';

// Helper functions
function getInitialCategories() {
	return SIZE_CATEGORIES.map(function mapCategoryKey(c) {
		return c.key;
	});
}

function mapUnitWithCategory(unit) {
	return { ...unit, _sizeCategory: categorizeUnit(unit) };
}

function checkCategoryMatch(unit, activeCategories) {
	return activeCategories.includes(unit._sizeCategory);
}

function checkUnitHasAnyAmenity(unit, activeAmenities) {
	return activeAmenities.some(function checkAmenity(amenity) {
		return unitHasAmenity(unit, amenity);
	});
}

function mapCategoryWithCount(category, unitsWithCategory) {
	return {
		...category,
		count: unitsWithCategory.filter(function filterByKey(u) {
			return u._sizeCategory === category.key;
		}).length,
	};
}

function mapAmenityWithCount(amenity, units) {
	return {
		key: amenity,
		label: amenity,
		count: units.filter(function filterByAmenity(u) {
			return unitHasAmenity(u, amenity);
		}).length,
	};
}

const DEFAULT_STORAGE_UNITS = [];

export function Filters({
	storageUnits = DEFAULT_STORAGE_UNITS,
	className,
	onFilteredUnitsChange,
}) {
	const [isOpen, setIsOpen] = useState(false);

	// Size filters state
	const [activeCategories, setActiveCategories] =
		useState(getInitialCategories);

	// Amenity filters state
	const availableAmenities = useMemo(
		function memoizedExtractAmenities() {
			return extractAmenities(storageUnits);
		},
		[storageUnits],
	);
	const [activeAmenities, setActiveAmenities] = useState(
		function getInitialAmenities() {
			return extractAmenities(storageUnits);
		},
	);

	useEffect(() => {
		setActiveAmenities(availableAmenities);
	}, [availableAmenities]);

	const [showFilters, setShowFilters] = useState(false);
	const [appliedFilteredUnits, setAppliedFilteredUnits] = useState([]);

	// Map units to categories once
	const unitsWithCategory = useMemo(
		function memoizedMapUnitsWithCategory() {
			return storageUnits.map(mapUnitWithCategory);
		},
		[storageUnits],
	);

	// Apply BOTH size and amenity filters
	const filteredUnits = useMemo(
		function memoizedFilterUnits() {
			let filtered = unitsWithCategory;

			// Apply size filters
			if (activeCategories.length > 0) {
				filtered = filtered.filter(function filterByCategory(u) {
					return checkCategoryMatch(u, activeCategories);
				});
			} else {
				filtered = [];
			}

			// Apply amenity filters
			const allAmenitiesSelected =
				activeAmenities.length === availableAmenities.length;

			if (availableAmenities.length > 0 && !allAmenitiesSelected) {
				if (activeAmenities.length > 0) {
					filtered = filtered.filter(
						function filterByAmenities(unit) {
							const unitAmenities =
								unit?.availableAmenities || [];
							return (
								unitAmenities.length === 0 ||
								checkUnitHasAnyAmenity(unit, activeAmenities)
							);
						},
					);
				} else {
					filtered = filtered.filter(
						function filterNoAmenities(unit) {
							const unitAmenities =
								unit?.availableAmenities || [];
							return unitAmenities.length === 0;
						},
					);
				}
			}

			return filtered;
		},
		[
			unitsWithCategory,
			activeCategories,
			activeAmenities,
			availableAmenities,
		],
	);

	const categoryMeta = useMemo(
		function memoizedCategoryMeta() {
			return SIZE_CATEGORIES.map(function mapCategory(c) {
				return mapCategoryWithCount(c, unitsWithCategory);
			});
		},
		[unitsWithCategory],
	);

	// Amenity metadata with count
	const amenityMeta = useMemo(
		function memoizedAmenityMeta() {
			return availableAmenities.map(function mapAmenity(amenity) {
				return mapAmenityWithCount(amenity, storageUnits);
			});
		},
		[availableAmenities, storageUnits],
	);

	const allSizesSelected = activeCategories.length === SIZE_CATEGORIES.length;
	const noSizesSelected = activeCategories.length === 0;
	const allAmenitiesSelected =
		activeAmenities.length === availableAmenities.length;
	const noAmenitiesSelected = activeAmenities.length === 0;

	// Notify parent of filtered units changes
	useEffect(() => {
		if (onFilteredUnitsChange && !showFilters) {
			onFilteredUnitsChange(filteredUnits);
			setAppliedFilteredUnits(filteredUnits);
		}
	}, [filteredUnits, onFilteredUnitsChange, showFilters]);

	const toggleCategory = useCallback(function toggleCategoryCallback(key) {
		setActiveCategories(function updateActiveCategories(prev) {
			if (prev.includes(key)) {
				return prev.filter(function removeKey(k) {
					return k !== key;
				});
			}
			return [...prev, key];
		});
	}, []);

	const toggleAmenity = useCallback(function toggleAmenityCallback(amenity) {
		setActiveAmenities(function updateActiveAmenities(prev) {
			if (prev.includes(amenity)) {
				return prev.filter(function removeAmenity(a) {
					return a !== amenity;
				});
			}
			return [...prev, amenity];
		});
	}, []);

	const handleSelectAllFilters = useCallback(
		function handleSelectAllFiltersCallback() {
			setActiveCategories(getInitialCategories());
			setActiveAmenities(availableAmenities);
		},
		[availableAmenities],
	);

	const handleClearAllFilters = useCallback(
		function handleClearAllFiltersCallback() {
			setActiveCategories([]);
			setActiveAmenities([]);
		},
		[],
	);

	const allSelected = allSizesSelected && allAmenitiesSelected;
	const noneSelected = noSizesSelected && noAmenitiesSelected;

	function handleApplyFiltersClick() {
		if (onFilteredUnitsChange) {
			onFilteredUnitsChange(filteredUnits);
		}
		setAppliedFilteredUnits(filteredUnits);
		setShowFilters(false);
	}

	function handleToggleFilters() {
		setShowFilters(!showFilters);
	}

	if (storageUnits.length === 0) {
		return null;
	}

	const filtersBlock = (
		<div className="filters-container">
			<div className="filter-header">
				<div className="filter-actions-wrapper">
					<span>Filters</span>
					<div className="filter-actions">
						<button
							type="button"
							className={classnames('action-link', {
								disabled: noneSelected,
							})}
							onClick={handleClearAllFilters}
							disabled={noneSelected}
						>
							Clear All
						</button>
						<button
							type="button"
							className={classnames('action-link', {
								disabled: allSelected,
							})}
							onClick={handleSelectAllFilters}
							disabled={allSelected}
						>
							Select All
						</button>
					</div>
				</div>
			</div>

			{/* Size Filters */}
			<div className="size-groups">
				<Paragraph className="text-light">SIZE</Paragraph>
				<ul className="size-category-list">
					{categoryMeta.map(function renderSizeCategory(cat, idx) {
						const checked = activeCategories.includes(cat.key);
						function handleCategoryChange() {
							toggleCategory(cat.key);
						}
						return (
							<li
								key={cat.key}
								className="size-category-item"
							>
								<Checkbox
									id={`unit-size-filter-${idx}`}
									name={`unit-size-${cat.key}`}
									checked={checked}
									value={cat.key}
									label={
										<span className="label-text">
											<Text className="label">
												{cat.label}
											</Text>
											<Text
												className="range"
												size="small"
												color="gray"
											>
												{' '}
												{cat.displayRange}
											</Text>
										</span>
									}
									onChange={handleCategoryChange}
								/>
							</li>
						);
					})}
				</ul>
				{/* this is a button to learn about storage unit sizes */}
				<button
					type="button"
					className="learn-sizes-link"
					onClick={() => setIsOpen(true)}
					aria-expanded={isOpen}
					aria-controls="unit-size-modal-content"
				>
					Learn more about storage sizes
				</button>
			</div>

			{/* Amenity Filters */}
			{availableAmenities.length > 0 && (
				<div className="amenity-groups">
					<div className="filter-section-header">
						<Paragraph className="text-light">FEATURES</Paragraph>
					</div>
					<ul className="amenity-list">
						{amenityMeta.map(
							function renderAmenityFilter(amenity, idx) {
								const checked = activeAmenities.includes(
									amenity.key,
								);
								function handleAmenityChange() {
									toggleAmenity(amenity.key);
								}
								return (
									<li
										key={amenity.key}
										className="amenity-item"
									>
										<Checkbox
											id={`amenity-filter-${idx}`}
											name={`amenity-${amenity.key}`}
											checked={checked}
											value={amenity.key}
											label={
												<span className="label-text">
													<Text
														className={classnames(
															'label',
															{
																unselected:
																	!checked,
															},
														)}
													>
														{amenity.label}
													</Text>
												</span>
											}
											onChange={handleAmenityChange}
										/>
									</li>
								);
							},
						)}
					</ul>
				</div>
			)}

			<Link
				to="/self-storage-sizes/"
				className="space-calculator"
			>
				<Image
					src={scale}
					alt="scale icon"
				/>
				<span className="space-calculator-text">
					How much space do you need?
				</span>
				<Image
					className="chevron"
					src={ChevronRight}
					alt=""
					aria-hidden="true"
				/>
			</Link>

			<Button
				onClick={handleApplyFiltersClick}
				className="apply-filters-button"
				secondary
				fullWidth
			>
				Apply Filters
			</Button>
		</div>
	);

	const availableUnitsHeader = (
		<div className="available-units-header">
			<H2 className="title-row">Available Units</H2>
			<span className="available-units-count">
				(
				{showFilters
					? appliedFilteredUnits.length
					: filteredUnits.length}
				)
			</span>
		</div>
	);

	return (
		<div className={classnames('filterable-units-container', className)}>
			<Modal
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				title="Storage Unit Sizes"
				bodyClassName="unit-size-modal-body"
			>
				<div id="unit-size-modal-content">
					<StorageUnitSizeModal />
				</div>
			</Modal>
			<div
				className={classnames('filters-modal-toggle', {
					'filters-open': showFilters,
				})}
			>
				{availableUnitsHeader}
				<Popup
					buttonContent={
						<Button
							white
							onClick={handleToggleFilters}
							className="filter-button-mobile"
						>
							<Image
								src={filtersImage}
								alt="Filter Icon"
							/>
							Filters
						</Button>
					}
					position="bottom-right"
					open={showFilters}
					onToggle={setShowFilters}
				>
					{filtersBlock}
				</Popup>
			</div>
			<aside className="filters-sidebar">
				{availableUnitsHeader}
				{filtersBlock}
			</aside>
		</div>
	);
}

Filters.propTypes = {
	storageUnits: PropTypes.arrayOf(PropTypes.object),
	className: PropTypes.string,
	onFilteredUnitsChange: PropTypes.func,
};

export {
	SIZE_CATEGORIES,
	parseSqft,
	categorizeUnit,
	extractAmenities,
	unitHasAmenity,
};

export default Filters;
